import {
  BellIcon,
  ChartBarIcon,
  CogIcon,
  CpuChipIcon,
  DocumentTextIcon,
  HomeIcon,
  UserCircleIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ArrowRightOnRectangleIcon,
  BriefcaseIcon,
  BeakerIcon,
  ScaleIcon,
  EyeIcon,
  AdjustmentsHorizontalIcon,
  KeyIcon,
} from "@heroicons/react/24/outline";
import React, { Fragment } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { useAuth } from "../contexts/AuthContext";
import { Avatar } from "./ui/Avatar";
import { Badge } from "./ui/Badge";

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  permission?: { resource: string; action: string };
  roles?: string[];
}

function navItemIsActive(pathname: string, href: string): boolean {
  if (href === "/analysis/trading-agents") {
    return pathname === href || pathname.startsWith(`${href}/`);
  }
  if (href === "/analysis") {
    if (pathname === "/analysis") return true;
    if (pathname.startsWith("/analysis/") && !pathname.startsWith("/analysis/trading-agents")) {
      return true;
    }
    return false;
  }
  return pathname === href;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission, hasRole } = useAuth();

  // Base navigation items
  const baseNavigation: NavItem[] = [
    { name: "Dashboard", href: "/dashboard", icon: HomeIcon },
    {
      name: "Analysis",
      href: "/analysis",
      icon: ChartBarIcon,
      permission: { resource: "analysis", action: "create" },
    },
    {
      name: "Trading Agents",
      href: "/analysis/trading-agents",
      icon: CpuChipIcon,
      permission: { resource: "analysis", action: "create" },
    },
    {
      name: "Portfolio",
      href: "/portfolio",
      icon: BriefcaseIcon,
      permission: { resource: "analysis", action: "read" },
    },
    {
      name: "Compare",
      href: "/compare",
      icon: ScaleIcon,
      permission: { resource: "analysis", action: "create" },
    },
    {
      name: "Backtest",
      href: "/backtest",
      icon: BeakerIcon,
      permission: { resource: "analysis", action: "create" },
    },
    {
      name: "Watchlists",
      href: "/watchlists",
      icon: EyeIcon,
      permission: { resource: "analysis", action: "read" },
    },
    {
      name: "Strategies",
      href: "/strategies",
      icon: AdjustmentsHorizontalIcon,
      permission: { resource: "analysis", action: "read" },
    },
    {
      name: "Reports",
      href: "/reports",
      icon: DocumentTextIcon,
      permission: { resource: "reports", action: "read" },
    },
    {
      name: "Team",
      href: "/team",
      icon: UsersIcon,
    },
    {
      name: "API Keys",
      href: "/api-keys",
      icon: KeyIcon,
    },
    {
      name: "Settings",
      href: "/settings",
      icon: CogIcon,
      permission: { resource: "settings", action: "read" },
    },
  ];

  // Admin navigation items
  const adminNavigation: NavItem[] = [
    {
      name: "Users",
      href: "/admin/users",
      icon: UsersIcon,
      roles: ["admin", "manager"],
    },
    {
      name: "Organization",
      href: "/admin/organization",
      icon: BuildingOfficeIcon,
      roles: ["admin"],
    },
  ];

  // Filter navigation based on permissions
  const visibleNavigation = baseNavigation.filter((item) => {
    if (!item.permission && !item.roles) return true;
    if (item.permission) {
      return hasPermission(item.permission.resource, item.permission.action);
    }
    if (item.roles) {
      return item.roles.some((role) => hasRole(role));
    }
    return false;
  });

  const visibleAdminNav = adminNavigation.filter((item) => {
    if (item.roles) {
      return item.roles.some((role) => hasRole(role));
    }
    return false;
  });

  const handleLogout = async () => {
    await logout();
  };

  const showDemoBanner =
    String(import.meta.env.VITE_SHOW_DEMO_BANNER || "").toLowerCase() ===
    "true";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {showDemoBanner ? (
        <div className="fixed inset-x-0 top-0 z-[60] border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-sm text-amber-900">
          Demo / YC feature preview — data may be simulated; not investment advice.
        </div>
      ) : null}
      {/* Sidebar */}
      <div
        className={`fixed left-0 z-50 w-64 border-r border-gray-200 bg-white shadow-soft-lg ${
          showDemoBanner ? "top-10 bottom-0" : "top-0 bottom-0"
        }`}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-center border-b border-gray-200 bg-gradient-to-r from-primary-600 to-primary-700">
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-white" />
            <span className="ml-2 text-xl font-bold text-white">Financial AI</span>
          </div>
        </div>

        {/* Organization Badge */}
        {user?.organization && (
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization
                </p>
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {user.organization.name}
                </p>
              </div>
              <Badge variant="primary" size="sm">
                {user.organization.subscription_tier}
              </Badge>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="mt-6 px-3 flex-1 overflow-y-auto">
          <div className="space-y-1">
            {visibleNavigation.map((item) => {
              const isActive = navItemIsActive(location.pathname, item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
                    isActive
                      ? "bg-primary-50 text-primary-700 shadow-sm"
                      : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  <item.icon
                    className={`mr-3 h-5 w-5 flex-shrink-0 ${
                      isActive
                        ? "text-primary-600"
                        : "text-gray-400 group-hover:text-gray-600"
                    }`}
                  />
                  {item.name}
                </Link>
              );
            })}
          </div>

          {/* Admin Section */}
          {visibleAdminNav.length > 0 && (
            <div className="mt-8">
              <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Administration
              </h3>
              <div className="mt-2 space-y-1">
                {visibleAdminNav.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
                        isActive
                          ? "bg-primary-50 text-primary-700 shadow-sm"
                          : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                      }`}
                    >
                      <item.icon
                        className={`mr-3 h-5 w-5 flex-shrink-0 ${
                          isActive
                            ? "text-primary-600"
                            : "text-gray-400 group-hover:text-gray-600"
                        }`}
                      />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </nav>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white">
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center w-full px-3 py-2 text-sm rounded-lg hover:bg-gray-50 transition-colors">
              <Avatar
                src={user?.avatar_url}
                fallback={user?.full_name}
                size="sm"
                className="mr-3"
              />
              <div className="flex-1 text-left min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.full_name || "User"}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.role.display_name}</p>
              </div>
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => navigate("/profile")}
                        className={`${
                          active ? "bg-gray-100" : ""
                        } flex items-center w-full px-4 py-2 text-sm text-gray-700`}
                      >
                        <UserCircleIcon className="mr-3 h-5 w-5 text-gray-400" />
                        Profile
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleLogout}
                        className={`${
                          active ? "bg-gray-100" : ""
                        } flex items-center w-full px-4 py-2 text-sm text-red-600`}
                      >
                        <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
                        Sign out
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>

      {/* Main content */}
      <div className={`pl-64 ${showDemoBanner ? "pt-10" : ""}`}>
        {/* Top bar */}
        <div
          className={`sticky z-40 border-b border-gray-200 bg-white/80 shadow-sm backdrop-blur-md ${
            showDemoBanner ? "top-10" : "top-0"
          }`}
        >
          <div className="flex h-16 items-center justify-between px-6">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">
                {visibleNavigation.find((item) => navItemIsActive(location.pathname, item.href))
                  ?.name ||
                  visibleAdminNav.find((item) => location.pathname === item.href)?.name ||
                  "Dashboard"}
              </h1>
            </div>

            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <button className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
                <BellIcon className="h-6 w-6" />
                <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-danger-500 ring-2 ring-white"></span>
              </button>

              {/* User Role Badge */}
              {user && (
                <Badge variant="primary" size="md">
                  {user.role.display_name}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-6">
          <div className="mx-auto max-w-7xl">{children}</div>
        </main>
      </div>
    </div>
  );
}
